import { notifications } from '@mantine/notifications'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { HttpStatusCode } from 'axios'

import { useUserStore } from '@/store/user.store'

import { OpenAPI as OpenAPIConfig } from '@/__generated__/requests'

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: import.meta.env.MODE === 'production' ? 3 : false,
        staleTime: 60 * 5 * 1000,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    return makeQueryClient()
  }

  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient()
  }

  return browserQueryClient
}

OpenAPIConfig.BASE = import.meta.env.VITE_BACKEND_URL || ''

OpenAPIConfig.WITH_CREDENTIALS = true

OpenAPIConfig.interceptors.response.use(response => {
  if (response.status === HttpStatusCode.Unauthorized) {
    useUserStore.getState().resetUser()
  }

  if (
    response.status !== HttpStatusCode.Ok &&
    response.status !== HttpStatusCode.Created &&
    response.status !== HttpStatusCode.Unauthorized &&
    response.status !== HttpStatusCode.NoContent &&
    response.status !== HttpStatusCode.Forbidden
  ) {
    notifications.show({
      color: 'red',
      message: response.data.message || 'Unknown error',
      title: response.data.error || 'Error',
    })
  }

  return response
})

export function ReactQueryProvider({
  children,
}: Readonly<{
  children: React.ReactNode
}>) {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      {children}

      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
